import type { LatLng } from '@fdt/shared';
import { Box } from '@mui/material';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useEffect, useRef } from 'react';

export interface RouteMapStop {
  id: string;
  label: string;
  location: LatLng | null;
  /** Shown in the marker; usually the stop's position in the route. */
  sequence: number;
  status: 'pending' | 'visited' | 'skipped';
}

interface RouteMapProps {
  stops: RouteMapStop[];
  /** The road geometry from the routing provider, if it has been computed. */
  geometry: LatLng[] | null;
  depot: LatLng | null;
  tileUrl: string;
  height?: number;
  /** Highlighted, e.g. because the matching list row is hovered or selected. */
  selectedStopId?: string | null;
  onStopClick?: (stopId: string) => void;
  /** Where the van actually went, drawn under the plan. */
  track?: LatLng[] | null;
  /** The playback cursor, if a track is being replayed. */
  cursor?: LatLng | null;
  /**
   * Stretches of the track to draw in the deviation colour, as index ranges
   * into `track`. Passed as ranges rather than coordinates so the highlight
   * cannot drift from the line it belongs to.
   */
  deviationRanges?: { from: number; to: number }[];
}

const STATUS_COLOUR: Record<RouteMapStop['status'], string> = {
  pending: '#15616d',
  visited: '#2e7d32',
  skipped: '#c62828',
};

/** A numbered circle, so the visiting order is readable straight off the map. */
function stopIcon(stop: RouteMapStop, selected: boolean): L.DivIcon {
  const size = selected ? 34 : 28;
  return L.divIcon({
    className: '',
    html: `<div style="
      width:${size}px;height:${size}px;border-radius:50%;
      background:${STATUS_COLOUR[stop.status]};
      border:${selected ? 3 : 2}px solid #fff;
      box-shadow:0 1px 4px rgba(0,0,0,.4);
      color:#fff;font:600 ${selected ? 14 : 12}px/${size - (selected ? 6 : 4)}px system-ui;
      text-align:center;">${stop.sequence}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

/** The playback cursor: deliberately unlike a stop marker. */
const CURSOR_ICON = L.divIcon({
  className: '',
  html: `<div style="
    width:20px;height:20px;border-radius:50%;background:#a8630a;
    border:3px solid #fff;box-shadow:0 1px 5px rgba(0,0,0,.5);"></div>`,
  iconSize: [20, 20],
  iconAnchor: [10, 10],
});

const DEPOT_ICON = L.divIcon({
  className: '',
  html: `<div style="
    width:26px;height:26px;border-radius:4px;background:#c1440e;
    border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.4);
    color:#fff;font:700 13px/22px system-ui;text-align:center;">D</div>`,
  iconSize: [26, 26],
  iconAnchor: [13, 13],
});

/**
 * Read-only map of a planned route: the depot, the stops in order, and the road
 * line once it has been computed.
 *
 * Leaflet is imperative, so the map lives in refs and the layers are rebuilt
 * whenever the inputs change. The alternative — reconciling individual markers
 * — is more code for a map that redraws in a millisecond.
 */
export function RouteMap({
  stops,
  geometry,
  depot,
  tileUrl,
  height = 420,
  selectedStopId,
  onStopClick,
  track,
  cursor,
  deviationRanges,
}: RouteMapProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const layersRef = useRef<L.LayerGroup | null>(null);
  /**
   * The playback cursor lives in its own layer group.
   *
   * Sharing one group would mean clearing and refitting the whole map on every
   * tick of the replay, which fights the viewer: the map would re-zoom away
   * from wherever they had panned, several times a second.
   */
  const cursorRef = useRef<L.LayerGroup | null>(null);
  const onStopClickRef = useRef(onStopClick);
  onStopClickRef.current = onStopClick;

  useEffect(() => {
    if (!containerRef.current || mapRef.current) {
      return;
    }

    const map = L.map(containerRef.current).setView([45.1349, 7.7428], 11);
    L.tileLayer(tileUrl, {
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap contributors',
    }).addTo(map);
    layersRef.current = L.layerGroup().addTo(map);
    cursorRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;

    // The map is often inside a panel that settles after the first paint.
    const timer = setTimeout(() => map.invalidateSize(), 250);

    return () => {
      clearTimeout(timer);
      map.remove();
      mapRef.current = null;
      layersRef.current = null;
      cursorRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    const layers = layersRef.current;
    if (!map || !layers) {
      return;
    }

    layers.clearLayers();

    // The recorded track goes down first, so the plan is drawn over what
    // happened rather than hidden by it.
    if (track && track.length > 1) {
      L.polyline(
        track.map((point) => [point.lat, point.lng] as [number, number]),
        { color: '#a8630a', weight: 3, opacity: 0.85 },
      ).addTo(layers);

      for (const range of deviationRanges ?? []) {
        const slice = track.slice(range.from, range.to + 1);
        if (slice.length > 1) {
          L.polyline(
            slice.map((point) => [point.lat, point.lng] as [number, number]),
            { color: '#b3261e', weight: 5, opacity: 0.9 },
          ).addTo(layers);
        }
      }
    }

    if (geometry && geometry.length > 1) {
      L.polyline(
        geometry.map((point) => [point.lat, point.lng] as [number, number]),
        { color: '#15616d', weight: 4, opacity: 0.75 },
      ).addTo(layers);
    } else if (stops.some((stop) => stop.location)) {
      // No road line yet: a dashed straight-line hint makes the order visible
      // without pretending it is a real route.
      const points = [
        ...(depot ? [depot] : []),
        ...stops.filter((stop) => stop.location).map((stop) => stop.location!),
        ...(depot ? [depot] : []),
      ];
      L.polyline(
        points.map((point) => [point.lat, point.lng] as [number, number]),
        { color: '#90a4ae', weight: 2, dashArray: '6 6' },
      ).addTo(layers);
    }

    if (depot) {
      L.marker([depot.lat, depot.lng], { icon: DEPOT_ICON, zIndexOffset: 500 }).addTo(layers);
    }



    for (const stop of stops) {
      if (!stop.location) {
        continue;
      }
      const marker = L.marker([stop.location.lat, stop.location.lng], {
        icon: stopIcon(stop, stop.id === selectedStopId),
        zIndexOffset: stop.id === selectedStopId ? 1000 : 0,
      })
        .bindTooltip(`${stop.sequence}. ${stop.label}`)
        .addTo(layers);

      marker.on('click', () => onStopClickRef.current?.(stop.id));
    }

    const bounds = L.latLngBounds(
      [
        ...(depot ? [depot] : []),
        ...stops.filter((stop) => stop.location).map((stop) => stop.location!),
        // The track is included, so a detour that left the planned area is
        // visible rather than off the edge of the map.
        ...(track ?? []),
      ].map((point) => [point.lat, point.lng] as [number, number]),
    );
    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 15 });
    }
  }, [stops, geometry, depot, selectedStopId, track, deviationRanges]);

  // The cursor alone, redrawn without touching the rest or the viewport.
  useEffect(() => {
    const layer = cursorRef.current;
    if (!layer) {
      return;
    }

    layer.clearLayers();
    if (cursor) {
      L.marker([cursor.lat, cursor.lng], { icon: CURSOR_ICON, zIndexOffset: 900 }).addTo(layer);
    }
  }, [cursor]);

  return (
    <Box
      ref={containerRef}
      sx={{
        height,
        borderRadius: 1,
        overflow: 'hidden',
        border: (theme) => `1px solid ${theme.palette.divider}`,
        '& .leaflet-pane': { zIndex: 1 },
        '& .leaflet-control': { zIndex: 2 },
      }}
    />
  );
}
